import { OnboardingModeRadioGroup } from "@components/utils/Auth";
import { OnboardCallbackProps } from "@components/utils/Auth/types";
import { SEON_FEATURE } from "@configs/seon";
import { OnboardMode } from "@constants";
import { useSeonSession } from "@hooks/useSeonSession";
import { getStoredLoginMethod } from "@utils/credentials";
import { AuthToken, Button } from "kz-ui-sdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdChevronRight, MdPerson, MdPhoneAndroid } from "react-icons/md";
import LoginUP from "../LoginUP";
import RegisterUP from "../RegisterUP";

export interface OnboardUPProps extends OnboardCallbackProps {
  onForgotSecret?: () => void;
  onLoginSuccess?: (token: AuthToken) => void;
  onRegisterAndLoginSuccess?: (token: AuthToken) => void;
  onPostLoginCheckFailed?: () => void;
  onPostLoginCheckPassed?: () => void;
}

const OnboardUP = ({
  defaultMode = OnboardMode.LOGIN,
  onModeChange,
  onForgotSecret,
  onRegisterAndLoginSuccess,
  onLoginSuccess,
  onPostLoginCheckFailed,
  onPostLoginCheckPassed,
  deferUpdateToken,
}: OnboardUPProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<OnboardMode>(defaultMode);
  const [loginMethod, setLoginMethod] = useState<"phone" | "username">(getStoredLoginMethod());
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number | undefined>(undefined);

  useSeonSession(SEON_FEATURE.ONBOARDING, {
    autoInit: true,
  });

  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const handleAccountLocked = (isLocked: boolean, remainingTime?: number) => {
    setIsAccountLocked(isLocked);
    if (isLocked) {
      setRemainingTime(remainingTime);
    }
  };

  const loginSwitchAriaLabel = useMemo(() => {
    return `Login mode switch ${loginMethod === "phone" ? "username-password" : "phone-otp"}`;
  }, [loginMethod]);

  return (
    <>
      <OnboardingModeRadioGroup
        value={mode}
        onChange={(value) => {
          setMode(value);
          onModeChange?.(value);
        }}
      />
      <div className="mt-4 flex w-full flex-col">
        {mode === OnboardMode.LOGIN && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLoginMethod(loginMethod === "phone" ? "username" : "phone")}
              classes={{
                "root&": "!border-none !bg-transparent w-fit self-center !h-[16px]",
              }}
              iconPosition="right"
              className="mt-2"
              icon={<MdChevronRight className="invert-x text-base text-content-base" />}
              disabled={loginMethod === "username" && isAccountLocked}
              aria-label={loginSwitchAriaLabel}
            >
              <div
                className="flex items-center justify-center gap-x-1"
                aria-label={loginSwitchAriaLabel}
              >
                {loginMethod === "phone" ? (
                  <MdPerson
                    className="text-base text-content-base"
                    aria-label={loginSwitchAriaLabel}
                  />
                ) : (
                  <MdPhoneAndroid
                    className="text-base text-content-base"
                    aria-label={loginSwitchAriaLabel}
                  />
                )}
                {loginMethod === "phone" ? t("login with username") : t("login with phone number")}
              </div>
            </Button>
            <div className="mt-6 flex flex-col items-center">
              <LoginUP
                defaultMethod={loginMethod}
                onAccountLocked={handleAccountLocked}
                isAccountLocked={isAccountLocked}
                lockedRemainingTime={remainingTime}
                onForgotSecret={onForgotSecret}
                onLoginSuccess={onLoginSuccess}
              />
            </div>
          </>
        )}

        {mode === OnboardMode.REGISTER && (
          <div className="flex animate-fade-in flex-col items-center">
            <RegisterUP
              onLoginSuccess={onRegisterAndLoginSuccess}
              onPostLoginCheckFailed={onPostLoginCheckFailed}
              onPostLoginCheckPassed={onPostLoginCheckPassed}
              deferUpdateToken={deferUpdateToken}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default OnboardUP;
